import React, { useState } from 'react';
import FileSaver from 'file-saver';
import autoTable from 'jspdf-autotable';
import axios from 'axios';
import { Workbook  } from 'exceljs';
import { jsPDF } from 'jspdf';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { sanitizeFilename } from '../../scripts/document.filename.script';
import { Company } from '../../models/company.model';
import { Expense } from '../../models/expense.model';
import { GlobalSwal } from '../../styles/swal.style';
import { ExpenseGroup } from '../../models/expense.group.model';
import { modalHide } from '../../utils/modal.util';
import { legendInvalid, legendValidInvalidRestart } from '../../utils/legend.data.util';
import { dateFormat } from '../../scripts/date.time.format.script';
import { accumulatedAmount } from '../../scripts/financial.calculation.script';
import { nameGroup } from '../../libraries/group.name.library';
import { formatContact } from '../../scripts/format.value.script';
import { validityBoolean } from '../../libraries/validation.library';
import { normalizeStatus, statusDocumentValue } from '../../libraries/status.document.library';
import { ExpenseDA } from '../../services/admin/expense.service';

import AppInputExportType from '../element/InputExportType';
import logo from '../../assets/themes/svg/logo-official.png';

export interface AppModalExpenseExportProps {
  companyForUser: Company | null,
  grouped: {state: boolean, desc: string},
  expenses: Expense[] | null,
  expenseGroups: ExpenseGroup[] | null,
  filter?: {interval: string, start_date: string, end_date: string, contact: string, treasury: string, validity: string, status: string, payment: string, sort_field: string, sort_mode: string, group: string, search: string},
  vatSubject?: boolean
};

const MODAL_ID = 'modal-expense-export'

const AppModalExpenseExport: React.FunctionComponent<AppModalExpenseExportProps> = ({companyForUser, grouped, expenses, expenseGroups, filter, vatSubject}) => {
  const {lang} = StateLanguage()

  const colCount = 7 + (vatSubject ? 1 : 0)
  const lastCol = vatSubject ? 'H' : 'G'

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [formatExport, setFormatExport] = useState({value: '', valid: false})

  const fileExcel = async (company: Company, tableTitle: string[][], tableData: any[][]) => {
    let workbook = new Workbook()
    let worksheet = workbook.addWorksheet('Expenses Data')

    worksheet.addRow([])
    worksheet.mergeCells(`A1:${lastCol}1`)
    worksheet.getRow(1).height = 35

    let imageBuffer = await axios.get(logo, {responseType: 'arraybuffer'})
    let imageLogotipo = workbook.addImage({buffer: imageBuffer.data, extension: 'png'})
    worksheet.addImage(imageLogotipo, { tl: {col: 0.1, row: 0.2}, ext: {width: 100, height: 30}})

    let titleRow = worksheet.addRow([`${company.name} - ${lang.labels.companyListOfExpenses}`])
    titleRow.font = {size: 18, bold: true}
    titleRow.getCell(1).alignment = {vertical:'distributed', horizontal: 'center'}
    worksheet.mergeCells(`A${titleRow.number}:${lastCol}${titleRow.number}`)
    worksheet.getRow(2).height = 30

    worksheet.addRow([])
    worksheet.mergeCells(`A3:${lastCol}3`)
    worksheet.getRow(3).height = 10

    let headerRow = worksheet.addRow(tableTitle[0])
    headerRow.font = {bold: true, color: {argb: 'FFFFFF'}}
    headerRow.eachCell((cell) => {
      cell.alignment = {vertical:'distributed', horizontal: 'left'}
      cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: '2980BA'}, bgColor: {argb: '2980BA'}}
    })
    worksheet.getRow(4).height = 20

    tableData.forEach((item) => {
      let bodyRow = worksheet.addRow(item)
      if (grouped.state && item.length === 1) {
        bodyRow.font = {bold: true}
      }
    })

    if (grouped.state) {
      for (let i = 0; i < tableData.length; i++) {
        if (tableData[i].length === 1) {
          worksheet.mergeCells(`A${i + 5}:${lastCol}${i + 5}`)
        }
      }
    }

    worksheet.getColumn(1).width = 17;
    worksheet.getColumn(2).width = 20;
    worksheet.getColumn(3).width = 25;
    worksheet.getColumn(4).width = 25;
    worksheet.getColumn(5).width = 13;
    worksheet.getColumn(6).width = 13;
    worksheet.getColumn(7).width = 17;
    if (vatSubject) { worksheet.getColumn(8).width = 15; }

    workbook.xlsx.writeBuffer().then(data => {
      let blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      FileSaver.saveAs(blob, `Expenses_of_${company.name.replaceAll(' ', '_')}.xlsx`)
    })
  }

  const filePdf = async (company: Company, tableTitle: string[][], tableData: any[][]) => {
    let document = new jsPDF('landscape', 'mm', [340, 220])

    if (grouped.state) {
      for (let i = 0; i < tableData.length; i++) {
        if (tableData[i].length === 1) {
          tableData[i][0] = {colSpan: colCount, content: tableData[i][0], styles: {fontStyle: 'bold'}}
        } else {
          const totalIdx = vatSubject ? 7 : 6
          if (vatSubject) { tableData[i][6] = {content: tableData[i][6], styles: {halign: 'right'}} }
          tableData[i][totalIdx] = {content: tableData[i][totalIdx], styles: {halign: 'right'}}
        }
      }
    } else {
      for (let i = 0; i < tableData.length; i++) {
        const totalIdx = vatSubject ? 7 : 6
        if (vatSubject) { tableData[i][6] = {content: tableData[i][6], styles: {halign: 'right'}} }
        tableData[i][totalIdx] = {content: tableData[i][totalIdx], styles: {halign: 'right'}}
      }
    }

    document.addImage(logo, 'PNG', 290, 5, 30, 9)
    document.setFontSize(18)
    document.setFont('helvetica', 'bold')
    document.text(`${company.name} - ${lang.labels.companyListOfExpenses}`, 25, 20)

    autoTable(document, {head: tableTitle, body: tableData, startY: 25})
    document.save(`Expenses_of_${sanitizeFilename(company.name).replaceAll(' ', '_')}.pdf`)
  }

  const buildTableData = (items: Expense[]) => {
    const tableData: any[][] = []
    for (let expense of items) {
      tableData.push([
        dateFormat(lang.code, expense.created_date),
        expense.expense_number,
        expense.concerning,
        formatContact(expense.provider) || '—',
        validityBoolean(lang, expense.validated),
        statusDocumentValue(lang, expense.status),
        ...(vatSubject ? [Number((expense.amount?.tax_total ?? 0).toFixed(2))] : []),
        Number((expense.amount?.total ?? 0).toFixed(2))
      ])
    }
    const acc = accumulatedAmount(items)
    tableData.push(['', '', '', '', '', '', ...(vatSubject ? [Number(acc.tax.toFixed(2))] : []), Number(acc.total.toFixed(2))])
    return tableData
  }

  const executeExport = async () => {
    setLoadIndicator('on')

    if (companyForUser && expenses && expenseGroups && formatExport.valid) {
      let tableTitle: string[][] = [[
        lang.labels.documentDate.toUpperCase(),
        lang.labels.expenseNumber.toUpperCase(),
        lang.labels.concerning.toUpperCase(),
        lang.labels.provider.toUpperCase(),
        lang.labels.validity.toUpperCase(),
        lang.labels.status.toUpperCase(),
        ...(vatSubject ? [lang.labels.vatAmount.toUpperCase()] : []),
        lang.labels.amountTotal.toUpperCase()
      ]]
      let tableData: any[][] = []

      if (grouped.state) {
        for (let i = 0; i < expenseGroups.length; i++) {
          tableData.push([`GROUP ${i + 1}: ${nameGroup(lang, grouped.desc, expenseGroups[i].group_detail).toUpperCase()}`])
          for (let expense of expenseGroups[i].expenses) {
            tableData.push([
              dateFormat(lang.code, expense.created_date),
              expense.expense_number,
              expense.concerning,
              formatContact(expense.provider) || '—',
              validityBoolean(lang, expense.validated),
              statusDocumentValue(lang, expense.status),
              ...(vatSubject ? [Number((expense.amount?.tax_total ?? 0).toFixed(2))] : []),
              Number((expense.amount?.total ?? 0).toFixed(2))
            ])
          }
          const acc = accumulatedAmount(expenseGroups[i].expenses)
          tableData.push(['', '', '', '', '', '', ...(vatSubject ? [Number(acc.tax.toFixed(2))] : []), Number(acc.total.toFixed(2))])
        }
      } else if (filter) {
        const response = await ExpenseDA.getExpenses(companyForUser.id, filter.interval, filter.start_date, filter.end_date, filter.contact, filter.treasury, filter.validity, filter.status, filter.payment, filter.sort_field, filter.sort_mode, '', 1, 20, filter.search, true)
        if (response.status === 200) {
          const allItems = (response.data.items ?? response.data.expenses ?? []).map((item: any) => ({ ...item, status: normalizeStatus(item.status) }))
          tableData = buildTableData(allItems)
        } else {
          tableData = buildTableData(expenses)
        }
      } else {
        tableData = buildTableData(expenses)
      }

      setTimeout(async () => {
        switch (formatExport.value) {
          case 'excel': fileExcel(companyForUser, tableTitle, tableData); break;
          case 'pdf': filePdf(companyForUser, tableTitle, tableData); break;
        }
        setLoadIndicator('off')
        executeHideModal()
      }, 1000)
    } else {
      GlobalSwal.fire({text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve, icon: 'error', showConfirmButton: false, timer: 2200}).then(() => {
        legendInvalid(`${MODAL_ID}-legend-format-export-type-required`)
        setLoadIndicator('off')
      })
    }
  }

  const executeHideModal = () => {
    modalHide(MODAL_ID)
    setTimeout(() => { restartModal() }, 200)
  }

  function restartModal() {
    legendValidInvalidRestart(`${MODAL_ID}-legend-format-export-type-required`)
    setFormatExport({value: '', valid: false})
  }

  return (
    <div id={MODAL_ID} className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.exportExpenses}</h2>
          </div>
          <div className="modal-body">
            <div className="form">
              <div className="fv-row">
                <label className="form-label required">{lang.labels.selectExportFormat}:</label>
                <AppInputExportType component={MODAL_ID} formatExport={formatExport} setFormatExport={setFormatExport}></AppInputExportType>
              </div>
            </div>
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModal}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={executeExport}>
              <span className="indicator-label">{lang.labels.export}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalExpenseExport;
